import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const QRResultPage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const dataParam = urlParams.get('data');
    
    if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        
        // Check if QR is expired (24 hours)
        const currentTime = new Date().getTime();
        const qrAge = currentTime - parsedData.timestamp;
        const twentyFourHours = 24 * 60 * 60 * 1000;

        if (qrAge > twentyFourHours) {
          setUserData({ error: 'QR Code has expired' });
        } else {
          setUserData(parsedData);
        }
      } catch (error) {
        setUserData({ error: 'Invalid QR Code data' });
      }
    } else {
      setUserData({ error: 'No QR data found' });
    }
    
    setLoading(false);
  }, [location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-cm-gold border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading QR Data...</p>
        </div>
      </div>
    );
  }

  if (userData?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-lg p-6 max-w-md w-full text-center">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{userData.error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-cm-gold text-cm-gold-ink px-6 py-2 rounded hover:bg-cm-gold-hover"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Header */}
      <div className="bg-cm-gold text-cm-gold-ink p-4 rounded-lg mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-xl font-bold">
            {userData.scanType === 'CHECK_OUT' ? 'Check-Out Complete' : 'User Verification'}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Check-Out Banner */}
      {userData.scanType === 'CHECK_OUT' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-center">
          <h4 className="font-bold text-blue-800 text-lg">Check-Out Recorded</h4>
          <p className="text-sm text-blue-700 mt-1">Player departure has been logged successfully.</p>
        </div>
      )}

      {/* User Info */}
      <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">User Information</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Name:</span>
            <span className="text-gray-800">{userData.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">ID:</span>
            <span className="text-gray-800">{userData.identifier}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Scanned:</span>
            <span className="text-gray-800 text-sm">{new Date().toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Verification Status */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h4 className="font-semibold text-gray-800 mb-4">Verification Status</h4>
        
        <div className="space-y-3">
          <div className={`p-3 rounded-lg border-l-4 ${userData.verification.aadhaar ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">Aadhaar Verification</span>
              <span className={`px-2 py-1 rounded text-sm ${userData.verification.aadhaar ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {userData.verification.aadhaar ? '✓ Verified' : '✗ Not Verified'}
              </span>
            </div>
          </div>

          <div className={`p-3 rounded-lg border-l-4 ${userData.verification.pan ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">PAN Verification</span>
              <span className={`px-2 py-1 rounded text-sm ${userData.verification.pan ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {userData.verification.pan ? '✓ Verified' : '✗ Not Verified'}
              </span>
            </div>
          </div>

          <div className={`p-3 rounded-lg border-l-4 ${userData.verification.bank ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">Bank Verification</span>
              <span className={`px-2 py-1 rounded text-sm ${userData.verification.bank ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {userData.verification.bank ? '✓ Verified' : '✗ Not Verified'}
              </span>
            </div>
          </div>
        </div>

        {/* Overall Status */}
        <div className={`mt-6 p-4 rounded-lg text-center ${userData.verification.isFullyVerified ? 'bg-green-100 border border-green-300' : 'bg-yellow-100 border border-yellow-300'}`}>
          <div className={`text-3xl mb-2 ${userData.verification.isFullyVerified ? 'text-green-600' : 'text-yellow-600'}`}>
            {userData.verification.isFullyVerified ? '✅' : '⚠️'}
          </div>
          <h4 className={`font-bold text-lg ${userData.verification.isFullyVerified ? 'text-green-800' : 'text-yellow-800'}`}>
            {userData.verification.isFullyVerified ? 'Fully Verified User' : 'Verification Incomplete'}
          </h4>
          <p className={`text-sm mt-1 ${userData.verification.isFullyVerified ? 'text-green-700' : 'text-yellow-700'}`}>
            {userData.verification.isFullyVerified 
              ? 'This user has completed all verification requirements'
              : 'This user needs to complete pending verification steps'
            }
          </p>
        </div>
      </div>

      {/* Scan Another Button */}
      <div className="mt-6">
        <button
          onClick={() => navigate('/qr-scanner')}
          className="w-full bg-cm-gold text-cm-gold-ink py-3 px-6 rounded-lg hover:bg-cm-gold-hover transition-colors font-medium"
        >
          Scan Another QR Code
        </button>
      </div>
    </div>
  );
};

export default QRResultPage;